import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import ModalPortal from './ModalPortal';

let modalScrollLockCount = 0;

function acquireScrollLock() {
  modalScrollLockCount += 1;
  document.body.classList.add('no-scroll');
}

function releaseScrollLock() {
  modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);
  if (modalScrollLockCount === 0) document.body.classList.remove('no-scroll');
}

export default function ModalShell({
  title,
  subtitle,
  eyebrow = 'Portal action',
  icon: Icon,
  tone = 'accent',
  size = 'md',
  onClose,
  children,
  className = '',
  bodyClassName = '',
}) {
  const titleId = useId();
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      const dialogs = Array.from(document.querySelectorAll('.app-modal[role="dialog"]'));
      if (dialogs[dialogs.length - 1] !== dialogRef.current) return;
      if (event.key === 'Escape') onCloseRef.current?.();
      if (event.key === 'Tab') {
        const focusable = Array.from(dialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])') || []);
        if (!focusable.length) { event.preventDefault(); return; }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    acquireScrollLock();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      releaseScrollLock();
      returnFocusRef.current?.focus?.();
    };
  }, []);

  return (
    <ModalPortal>
      <div className="modal-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
        <section
          ref={dialogRef}
          className={`modal app-modal app-modal-${size} app-modal-tone-${tone} ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <header className="app-modal-header">
            {Icon && <span className="app-modal-icon"><Icon size={19} /></span>}
            <div className="app-modal-heading">
              {eyebrow && <span className="app-modal-eyebrow">{eyebrow}</span>}
              <h2 id={titleId}>{title}</h2>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <button ref={closeRef} type="button" className="app-modal-close" onClick={onClose} aria-label="Close popup" title="Close">
              <X size={18} />
            </button>
          </header>
          <div className={`app-modal-body ${bodyClassName}`.trim()}>{children}</div>
        </section>
      </div>
    </ModalPortal>
  );
}
